// Cave84 — Vintage Charts Page (Phase 3 Week 11 — Region-First Navigation)
// Spec §4.11 (F11). Three-level navigation:
//   Level 0: 20 region cards with sparkline dots + stats + bottle counts
//   Level 1: Region detail table (all 55 years, score/status filters, sticky header)
//   Level 2: Narrative side-panel (Hebrew text, producer notes)

import { useState, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { VintageChart, QualityLabel, VintageStyleCharacter } from '@cave84/shared-types';
import { useAllVintageCharts, useEnqueueVintageChartYear } from '@/hooks/useVintageCharts';
import { useIsOwner } from '@/stores/auth.store';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useBottleCountByVintageChartRegion } from '@/hooks/useVintageChartBottleCounts';
import { useCustomWineRegions } from '@/hooks/useCustomWineRegions';

// ─── Constants ────────────────────────────────────────────────────────────────

const REGIONS: { id: string; label: string; sub: string }[] = [
  { id: 'Burgundy_Red',    label: 'Burgundy Red',        sub: 'Côte de Nuits · Côte de Beaune' },
  { id: 'Burgundy_White',  label: 'Burgundy White',      sub: 'Côte de Beaune Blanc' },
  { id: 'Bordeaux_Left',   label: 'Bordeaux Left Bank',  sub: 'Médoc · Pessac-Léognan' },
  { id: 'Bordeaux_Right',  label: 'Bordeaux Right Bank', sub: 'Saint-Émilion · Pomerol' },
  { id: 'Sauternes',       label: 'Sauternes',           sub: 'Barsac · Sauternes' },
  { id: 'Rhone_North',     label: 'Rhône North',         sub: 'Côte-Rôtie · Hermitage · Cornas' },
  { id: 'Rhone_South',     label: 'Rhône South',         sub: 'Châteauneuf-du-Pape · Gigondas' },
  { id: 'Champagne',       label: 'Champagne',           sub: 'Vintage years only' },
  { id: 'Barolo',          label: 'Barolo',              sub: 'Piedmont' },
  { id: 'Brunello',        label: 'Brunello',            sub: 'Tuscany · vintage years only' },
  { id: 'Bolgheri',        label: 'Bolgheri',            sub: 'Super-Tuscan' },
  { id: 'Loire',           label: 'Loire',               sub: 'Sancerre · Vouvray · Chinon' },
  { id: 'Alsace',          label: 'Alsace',              sub: 'Riesling · Gewürztraminer' },
  { id: 'Mosel_Rheingau',  label: 'Mosel / Rheingau',   sub: 'Riesling' },
  { id: 'Rioja',           label: 'Rioja',               sub: 'Tempranillo · Alta · Alavesa' },
  { id: 'Ribera_del_Duero',label: 'Ribera del Duero',    sub: 'Tempranillo (Tinto Fino)' },
  { id: 'Priorat',         label: 'Priorat',             sub: 'Garnacha · Cariñena · llicorella' },
  { id: 'Tokaji',          label: 'Tokaji',              sub: 'Aszú · Eszencia' },
  { id: 'Nahe',            label: 'Nahe',                sub: 'Riesling' },
  { id: 'Amarone',         label: 'Amarone',             sub: 'Valpolicella Classico' },
];

const CURRENT_YEAR = new Date().getFullYear();
const START_YEAR   = 1971;
const ALL_YEARS: number[] = Array.from(
  { length: CURRENT_YEAR - START_YEAR + 1 },
  (_, i) => CURRENT_YEAR - i        // descending: newest first
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function scoreColor(score: number | null, quality: QualityLabel): string {
  if (score === null || quality === 'not_declared') return 'bg-surface-2 text-text-secondary';
  // Warm wine-palette: burgundy → amber → gold → warm slate
  if (score >= 95) return 'bg-[#5C1825] text-[#FFCFA8] font-semibold';
  if (score >= 90) return 'bg-[#C49255] text-[#FFE0CC] font-semibold';
  if (score >= 85) return 'bg-[#7A3B1E] text-[#FFD9B0] font-medium';
  if (score >= 80) return 'bg-[#7D5A1A] text-[#FFEBB0] font-medium';
  if (score >= 75) return 'bg-[#7A6820] text-[#FFF5D0]';
  if (score >= 70) return 'bg-[#62583A] text-[#F5EDD0]';
  if (score >= 65) return 'bg-[#5C5040] text-[#E8DDD0]';
  if (score >= 55) return 'bg-[#4E4858] text-[#DDD8E8]';
  return 'bg-[#3E4050] text-[#D0D4E0]';
}

function scoreDotColor(score: number | null): string {
  if (score === null) return '#404858';
  // Warm wine-palette for sparkline dots
  if (score >= 95) return '#C49255';
  if (score >= 90) return '#A0422C';
  if (score >= 85) return '#B87030';
  if (score >= 80) return '#C9A020';
  if (score >= 75) return '#9E8838';
  if (score >= 70) return '#786850';
  if (score >= 65) return '#5E5850';
  return '#4E4858';
}

function qualityBadgeColor(q: QualityLabel): string {
  // Warm wine palette — no green
  const map: Record<QualityLabel, string> = {
    exceptional:  'bg-[#5C1825]/70 text-[#FFCFA8] border border-[#C49255]/60',
    excellent:    'bg-[#C49255]/50 text-[#FFD9B8] border border-[#C49255]/50',
    very_good:    'bg-[#7A3B1E]/50 text-[#F5C890] border border-[#A05028]/50',
    good:         'bg-[#7D5A1A]/50 text-[#F0D888] border border-[#A07820]/50',
    average:      'bg-[#62583A]/50 text-[#D8CC98] border border-[#7A6848]/50',
    poor:         'bg-[#4E4858]/50 text-[#C8C0D8] border border-[#6E6880]/50',
    not_declared: 'bg-[#1B202B]/60 text-[#9C9483] border border-[#2F3645]/60',
  };
  return map[q] ?? map.not_declared;
}

function qualityLabel(q: QualityLabel): string {
  const map: Record<QualityLabel, string> = {
    exceptional:  'Exceptional',
    excellent:    'Excellent',
    very_good:    'Very Good',
    good:         'Good',
    average:      'Average',
    poor:         'Poor',
    not_declared: 'Not Declared',
  };
  return map[q] ?? q;
}

function styleCharLabel(s: VintageStyleCharacter): string {
  const map: Record<VintageStyleCharacter, string> = {
    ripe:         'Ripe',
    classic:      'Classic',
    structured:   'Structured',
    atypical:     'Atypical',
    heterogeneous:'Heterogeneous',
    cool:         'Cool',
    hot:          'Hot',
    extreme_heat: 'Extreme heat',
    extreme_cold: 'Extreme cold',
    not_declared: '—',
  };
  return map[s] ?? s;
}

function styleCharLabelHe(s: VintageStyleCharacter): string {
  const map: Record<VintageStyleCharacter, string> = {
    ripe:         'בשל',
    classic:      'קלאסי',
    structured:   'מובנה',
    atypical:     'חריג',
    heterogeneous:'לא אחיד',
    cool:         'קרירה',
    hot:          'חמה',
    extreme_heat: 'חום קיצוני',
    extreme_cold: 'קור קיצוני',
    not_declared: '—',
  };
  return map[s] ?? s;
}

function drinkingStatusLabel(s: string): string {
  const map: Record<string, string> = {
    too_young:    'Too young',
    developing:   'Developing',
    peak:         'Peak',
    declining:    'Declining',
    over_the_hill:'Over the hill',
  };
  return map[s] ?? s;
}

function drinkingStatusColor(s: string): string {
  const map: Record<string, string> = {
    too_young:    'text-sky-400',
    developing:   'text-blue-400',
    peak:         'text-emerald-400',
    declining:    'text-red-400',
    over_the_hill:'text-red-600',
  };
  return map[s] ?? 'text-text-secondary';
}

// ─── Region Stats ─────────────────────────────────────────────────────────────

interface RegionStats {
  bestVintage: { year: number; score: number } | null;
  mostRecentPeak: { year: number; score: number } | null;
  sparklinePoints: { year: number; score: number | null }[];
}

function computeRegionStats(regionId: string, chartMap: Map<string, VintageChart>): RegionStats {
  const all: VintageChart[] = ALL_YEARS
    .map((y) => chartMap.get(`${regionId}_${y}`))
    .filter((c): c is VintageChart => !!c);

  const scored = all.filter((c) => c.score !== null);

  const bestVintage = scored.length > 0
    ? scored.reduce((best, c) => (c.score! > best.score! ? c : best))
    : null;

  const recentPeaks = scored
    .filter((c) => c.score! >= 93 && c.year >= CURRENT_YEAR - 15)
    .sort((a, b) => b.year - a.year);
  const mostRecentPeak = recentPeaks[0] ?? null;

  const sparklinePoints = ALL_YEARS
    .slice(0, 15)
    .map((y) => ({ year: y, score: chartMap.get(`${regionId}_${y}`)?.score ?? null }))
    .filter((p) => !!chartMap.get(`${regionId}_${p.year}`))
    .slice(0, 10);

  return {
    bestVintage: bestVintage ? { year: bestVintage.year, score: bestVintage.score! } : null,
    mostRecentPeak: mostRecentPeak ? { year: mostRecentPeak.year, score: mostRecentPeak.score! } : null,
    sparklinePoints,
  };
}

// ─── Sparkline Dots ───────────────────────────────────────────────────────────

function SparklineDots({ points }: { points: { year: number; score: number | null }[] }) {
  if (points.length === 0) {
    return <div className="h-5 flex items-center"><span className="text-xs text-text-secondary">No data</span></div>;
  }
  return (
    <div className="flex items-end gap-1 h-6">
      {[...points].reverse().map((p) => (
        <div
          key={p.year}
          title={`${p.year}: ${p.score ?? 'ND'}`}
          className="w-2.5 rounded-sm flex-shrink-0"
          style={{
            backgroundColor: scoreDotColor(p.score),
            height: p.score ? `${Math.round(((p.score - 55) / 45) * 20) + 4}px` : '4px',
            minHeight: '4px',
            maxHeight: '24px',
          }}
        />
      ))}
    </div>
  );
}

// ─── Region Card ──────────────────────────────────────────────────────────────

function RegionCard({
  region,
  stats,
  bottleCount,
  onClick,
}: {
  region: typeof REGIONS[0];
  stats: RegionStats;
  bottleCount: number;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="group text-left border border-border-medium rounded-xl p-5 hover:border-accent-burgundy transition-all duration-150 hover:shadow-lg hover:shadow-black/20 w-full"
      style={{ background: '#262118' }}
    >
      <div className="mb-3">
        <h3 className="text-xl font-bold text-text-primary leading-tight" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
          {region.label}
        </h3>
        <p className="text-xs text-text-secondary mt-0.5 truncate">{region.sub}</p>
      </div>

      <div className="mb-3">
        <SparklineDots points={stats.sparklinePoints} />
      </div>

      <div className="space-y-1.5 border-t border-border-medium pt-3 mt-1">
        {stats.bestVintage && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-text-secondary">Best vintage</span>
            <span className="text-xs font-semibold text-text-primary font-mono">
              {stats.bestVintage.year}{' '}
              <span className="text-emerald-400">({stats.bestVintage.score})</span>
            </span>
          </div>
        )}
        {stats.mostRecentPeak && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-text-secondary">Most recent peak</span>
            <span className="text-xs font-semibold text-text-primary font-mono">
              {stats.mostRecentPeak.year}{' '}
              <span className="text-emerald-400">({stats.mostRecentPeak.score})</span>
            </span>
          </div>
        )}
        {bottleCount > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-text-secondary">Bottles in cellar</span>
            <span className="text-xs font-semibold text-text-primary font-mono">{bottleCount}</span>
          </div>
        )}
        {!stats.bestVintage && !stats.mostRecentPeak && (
          <p className="text-xs text-text-secondary italic">No data yet</p>
        )}
      </div>
    </button>
  );
}

// ─── Narrative Detail Panel ───────────────────────────────────────────────────

function DetailPanel({ entry, onClose }: { entry: VintageChart; onClose: () => void }) {
  const regionLabel = REGIONS.find((r) => r.id === entry.region)?.label ?? entry.region;

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-start justify-between gap-4 p-5 border-b border-border-medium">
        <div>
          <p className="text-xs text-text-secondary uppercase tracking-widest mb-1">{regionLabel}</p>
          <h2 className="text-3xl font-bold text-text-primary" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
            {entry.year}
          </h2>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {entry.score !== null && (
              <span className="text-lg font-semibold text-text-primary">{entry.score}</span>
            )}
            <span className={'text-sm px-2.5 py-0.5 rounded-full ' + qualityBadgeColor(entry.quality_label)}>
              {qualityLabel(entry.quality_label)}
            </span>
            <span className={'text-sm font-medium ' + drinkingStatusColor(entry.drinking_status)}>
              {drinkingStatusLabel(entry.drinking_status)}
            </span>
          </div>
        </div>
        <button onClick={onClose} className="text-text-secondary hover:text-text-primary transition-colors mt-0.5 flex-shrink-0" aria-label="Close">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-6">
        <div>
          <h3 className="text-xs text-text-secondary uppercase tracking-widest mb-2">Vintage Narrative</h3>
          <div
            dir="rtl" lang="he"
            className="text-base text-text-primary leading-loose bg-surface-1 rounded-lg p-5 border border-border-medium"
            style={{ fontFamily: 'Frank Ruhl Libre, David, serif' }}
          >
            {entry.weather_narrative_he}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-text-secondary uppercase tracking-widest mb-1">Style</p>
            <p className="text-base text-text-primary">
              {styleCharLabel(entry.style_character)}{' '}
              <span className="text-text-secondary text-sm">({styleCharLabelHe(entry.style_character)})</span>
            </p>
          </div>
          <div>
            <p className="text-xs text-text-secondary uppercase tracking-widest mb-1">Drinking window</p>
            <p className={'text-base font-medium ' + drinkingStatusColor(entry.drinking_status)}>
              {drinkingStatusLabel(entry.drinking_status)}
            </p>
          </div>
        </div>

        {entry.notable_producers_overperformed && (
          <div>
            <h3 className="text-xs text-text-secondary uppercase tracking-widest mb-2">Notable overperformers</h3>
            <div dir="rtl" lang="he" className="text-base text-text-primary leading-relaxed" style={{ fontFamily: 'Frank Ruhl Libre, David, serif' }}>
              {entry.notable_producers_overperformed}
            </div>
          </div>
        )}

        {entry.comparison_to_adjacent && (
          <div>
            <h3 className="text-xs text-text-secondary uppercase tracking-widest mb-2">vs. adjacent vintages</h3>
            <div dir="rtl" lang="he" className="text-base text-text-primary leading-relaxed" style={{ fontFamily: 'Frank Ruhl Libre, David, serif' }}>
              {entry.comparison_to_adjacent}
            </div>
          </div>
        )}

        <div className="pt-2 border-t border-border-medium">
          <p className="text-xs text-text-secondary">
            Generated by <span className="capitalize">{entry.generated_by.replace('_', ' ')}</span>
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Add Year Modal ───────────────────────────────────────────────────────────

function AddYearModal({
  onClose, onAdd, existingYears, defaultRegion, regions,
}: {
  onClose: () => void;
  onAdd: (region: string, year: number) => void;
  existingYears: Set<string>;
  defaultRegion?: string;
  regions: typeof REGIONS;
}) {
  const [region, setRegion] = useState(defaultRegion ?? regions[0]?.id ?? '');
  const [year, setYear]     = useState<number>(CURRENT_YEAR);
  const alreadyExists = existingYears.has(`${region}_${year}`);

  return (
    <div role="button" tabIndex={0} aria-label="Close" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClose(); } }}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- dialog container; onClick only isolates the backdrop click */}
      <div className="bg-surface-2 rounded-xl border border-border-medium p-6 w-full max-w-sm mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-text-primary mb-4">Add Vintage Year</h2>
        <div className="space-y-4">
          <div>
            <span className="block text-xs text-text-secondary uppercase tracking-widest mb-1.5">Region</span>
            <select value={region} onChange={(e) => setRegion(e.target.value)} className="w-full bg-surface-1 border border-border-medium rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-accent-burgundy">
              {regions.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <span className="block text-xs text-text-secondary uppercase tracking-widest mb-1.5">Year</span>
            <input type="number" value={year} min={START_YEAR} max={CURRENT_YEAR + 1} onChange={(e) => setYear(Number(e.target.value))} className="w-full bg-surface-1 border border-border-medium rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-accent-burgundy" />
          </div>
          {alreadyExists && <p className="text-xs text-amber-400">This region/year combination is already in the charts.</p>}
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2 rounded-lg border border-border-medium text-sm text-text-secondary hover:text-text-primary transition-colors">Cancel</button>
          <button onClick={() => !alreadyExists && onAdd(region, year)} disabled={alreadyExists} className="flex-1 px-4 py-2 rounded-lg bg-accent-burgundy hover:bg-accent-burgundy/80 text-white text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed">Queue Generation</button>
        </div>
      </div>
    </div>
  );
}

// ─── Region Detail View (Level 1) ─────────────────────────────────────────────

function RegionDetailView({
  region, chartMap, onBack, onSelectEntry, selectedEntry, isOwner, onAddYear,
}: {
  region: typeof REGIONS[0];
  chartMap: Map<string, VintageChart>;
  onBack: () => void;
  onSelectEntry: (entry: VintageChart | null) => void;
  selectedEntry: VintageChart | null;
  isOwner: boolean;
  onAddYear: () => void;
}) {
  const isMobile = useIsMobile();
  const [minScore,      setMinScore]      = useState<number>(0);
  const [statusFilter,  setStatusFilter]  = useState<string>('');
  const [panelWidth,    setPanelWidth]    = useState<number>(480);
  const panelWidthRef = useRef(480);
  panelWidthRef.current = panelWidth;

  const onResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = panelWidthRef.current;
    const onMove = (ev: MouseEvent) => {
      const delta = startX - ev.clientX;
      setPanelWidth(Math.max(320, Math.min(800, startW + delta)));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  // All 55 years (1971–CURRENT_YEAR), newest first. Show entry or null (pending).
  const allRows = useMemo(
    () => ALL_YEARS.map((y) => ({ year: y, entry: chartMap.get(`${region.id}_${y}`) ?? null })),
    [region.id, chartMap]
  );

  // Apply filters — only to rows that have data; pending rows are shown as-is
  const filteredRows = useMemo(
    () => allRows.filter(({ entry }) => {
      if (!entry) return true; // always show pending rows
      if (minScore > 0 && (entry.score === null || entry.score < minScore)) return false;
      if (statusFilter && entry.drinking_status !== statusFilter) return false;
      return true;
    }),
    [allRows, minScore, statusFilter]
  );

  const hasFilters = minScore > 0 || statusFilter !== '';

  // Mobile: narrative replaces the table full-screen instead of sitting beside it
  // in a fixed-width panel (which overflowed the viewport — CAVE84-mobile-cutoff).
  if (isMobile && selectedEntry) {
    return (
      <div className="flex-1 overflow-hidden h-full flex flex-col bg-surface-2">
        <DetailPanel entry={selectedEntry} onClose={() => onSelectEntry(null)} />
      </div>
    );
  }

  return (
    <div className="flex flex-1 overflow-hidden h-full">
      {/* ── Table column ── */}
      <div className="flex-1 overflow-auto">
        <div className="px-6 py-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-2">
            <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary transition-colors group">
              <svg className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              All Regions
            </button>
            <span className="text-text-secondary text-sm">/</span>
            <span className="text-sm text-text-primary">{region.label}</span>
          </div>

          {/* Region title */}
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <h2 className="text-4xl font-bold text-text-primary leading-tight" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
                {region.label}
              </h2>
              <p className="text-sm text-text-secondary mt-1">{region.sub}</p>
            </div>
            {isOwner && (
              <button onClick={onAddYear} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-accent-burgundy hover:bg-accent-burgundy/80 text-white text-sm font-medium transition-colors flex-shrink-0">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Year
              </button>
            )}
          </div>

          {/* Filter bar */}
          <div className="flex items-center gap-4 mb-4 flex-wrap">
            {/* Min score */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-text-secondary whitespace-nowrap">Min score</span>
              <input
                type="range" min={0} max={100} step={5} value={minScore}
                onChange={(e) => setMinScore(Number(e.target.value))}
                className="w-28 accent-accent-burgundy"
              />
              <span className="text-xs font-mono text-text-primary w-8">{minScore > 0 ? minScore : 'All'}</span>
            </div>

            {/* Status filter */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-text-secondary whitespace-nowrap">Status</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-surface-1 border border-border-medium rounded px-2 py-1 text-xs text-text-primary focus:outline-none focus:border-accent-burgundy"
              >
                <option value="">All</option>
                <option value="too_young">Too young</option>
                <option value="developing">Developing</option>
                <option value="peak">Peak</option>
                <option value="declining">Declining</option>
                <option value="over_the_hill">Over the hill</option>
              </select>
            </div>

            {/* Clear filters */}
            {hasFilters && (
              <button onClick={() => { setMinScore(0); setStatusFilter(''); }} className="text-xs text-text-secondary hover:text-text-primary underline">
                Clear
              </button>
            )}

            {/* Row count */}
            <span className="text-xs text-text-secondary ml-auto">
              {filteredRows.filter(r => r.entry).length} / {allRows.filter(r => r.entry).length} vintages
            </span>
          </div>

          {/* Years list — card rows on mobile (the fixed-width, 5-column table below
              is wider than any phone viewport and was clipping on the right edge),
              real table on desktop. */}
          {isMobile ? (
            <div className="rounded-lg border border-border-medium divide-y divide-border-medium/40 overflow-hidden">
              {filteredRows.map(({ year, entry }) => {
                if (!entry) {
                  return (
                    <div key={year} className="flex items-center justify-between px-4 py-3">
                      <span className="font-mono text-base text-text-secondary/50">{year}</span>
                      <span className="text-sm text-text-secondary/40 italic">—</span>
                    </div>
                  );
                }

                const isSelected = selectedEntry?.id === entry.id;
                return (
                  <button
                    key={year}
                    type="button"
                    onClick={() => onSelectEntry(isSelected ? null : entry)}
                    className={
                      'w-full text-left flex items-center justify-between gap-2 px-4 py-3 transition-colors ' +
                      (isSelected ? 'bg-accent-burgundy/10' : 'active:bg-surface-2/70')
                    }
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-lg text-text-primary font-semibold tracking-tight flex-shrink-0">{entry.year}</span>
                      {entry.score !== null ? (
                        <span className={'inline-block text-sm font-mono px-2 py-0.5 rounded flex-shrink-0 ' + scoreColor(entry.score, entry.quality_label)}>
                          {entry.score}
                        </span>
                      ) : (
                        <span className="text-sm text-text-secondary flex-shrink-0">ND</span>
                      )}
                      <span className={'text-xs px-2 py-0.5 rounded-full truncate ' + qualityBadgeColor(entry.quality_label)}>
                        {qualityLabel(entry.quality_label)}
                      </span>
                    </div>
                    <span className={'text-xs font-medium text-right flex-shrink-0 ' + drinkingStatusColor(entry.drinking_status)}>
                      {drinkingStatusLabel(entry.drinking_status)}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="rounded-lg border border-border-medium overflow-hidden">
              <table className="w-full border-collapse table-fixed">
                <thead className="sticky top-0 z-10" style={{ background: '#262118' }}>
                  <tr>
                    <th className="text-left px-4 py-3 text-xs text-text-secondary uppercase tracking-widest font-semibold w-20">Year</th>
                    <th className="text-center px-4 py-3 text-xs text-text-secondary uppercase tracking-widest font-semibold w-20">Score</th>
                    <th className="text-left px-4 py-3 text-xs text-text-secondary uppercase tracking-widest font-semibold w-40">Quality</th>
                    <th className="text-center px-4 py-3 text-xs text-text-secondary uppercase tracking-widest font-semibold w-24">Style</th>
                    <th className="text-left px-4 py-3 text-xs text-text-secondary uppercase tracking-widest font-semibold w-28">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map(({ year, entry }) => {
                    if (!entry) {
                      // Pending row — year not yet seeded
                      return (
                        <tr key={year} className="border-t border-border-medium/30">
                          <td className="px-4 py-4 font-mono text-base text-text-secondary/50">{year}</td>
                          <td colSpan={4} className="px-4 py-4 text-sm text-text-secondary/40 italic">—</td>
                        </tr>
                      );
                    }

                    const isSelected = selectedEntry?.id === entry.id;
                    return (
                      <tr
                        key={year}
                        onClick={() => onSelectEntry(isSelected ? null : entry)}
                        className={
                          'border-t border-border-medium/50 cursor-pointer transition-colors ' +
                          (isSelected
                            ? 'bg-accent-burgundy/10 border-l-2 border-l-accent-burgundy'
                            : 'hover:bg-surface-2/70')
                        }
                      >
                        <td className="px-4 py-4 font-mono text-xl text-text-primary font-semibold tracking-tight">{entry.year}</td>
                        <td className="px-4 py-4 text-center">
                          {entry.score !== null ? (
                            <span className={'inline-block text-base font-mono px-3 py-1 rounded ' + scoreColor(entry.score, entry.quality_label)}>
                              {entry.score}
                            </span>
                          ) : (
                            <span className="text-base text-text-secondary">ND</span>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <span className={'text-sm px-2.5 py-1 rounded-full ' + qualityBadgeColor(entry.quality_label)}>
                            {qualityLabel(entry.quality_label)}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span dir="rtl" lang="he" className="text-base text-text-secondary" style={{ fontFamily: 'Frank Ruhl Libre, David, serif' }}>
                            {styleCharLabelHe(entry.style_character)}
                          </span>
                        </td>
                        <td className={'px-4 py-4 text-base font-medium ' + drinkingStatusColor(entry.drinking_status)}>
                          {drinkingStatusLabel(entry.drinking_status)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Resize handle + Narrative panel (desktop only — mobile renders the panel full-screen above) ── */}
      {!isMobile && selectedEntry && (
        <>
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- custom drag-to-resize splitter handle */}
          <div
            onMouseDown={onResizeStart}
            className="relative flex-shrink-0 w-1 bg-border-medium hover:bg-accent-burgundy/60 cursor-col-resize transition-colors"
            title="Drag to resize"
          >
            <div className="absolute inset-y-0 -left-2 -right-2" />
          </div>
          <div
            className="flex-shrink-0 bg-surface-2 overflow-hidden flex flex-col"
            style={{ width: `${panelWidth}px` }}
          >
            <DetailPanel entry={selectedEntry} onClose={() => onSelectEntry(null)} />
          </div>
        </>
      )}
    </div>
  );
}

// ─── Region Grid (Level 0) ────────────────────────────────────────────────────

function RegionGrid({
  chartMap, regions, onSelectRegion, isOwner, onShowAddYear, bottleCounts,
}: {
  chartMap: Map<string, VintageChart>;
  regions: typeof REGIONS;
  onSelectRegion: (regionId: string) => void;
  isOwner: boolean;
  onShowAddYear: () => void;
  bottleCounts: Map<string, number>;
}) {
  const regionStats = useMemo(() => {
    const m = new Map<string, RegionStats>();
    for (const r of regions) m.set(r.id, computeRegionStats(r.id, chartMap));
    return m;
  }, [chartMap, regions]);

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-screen-xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <p className="text-xs text-text-secondary uppercase tracking-widest mb-1">Reference</p>
            <h1 className="text-3xl font-bold text-text-primary" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
              Vintage Charts
            </h1>
            <p className="text-sm text-text-secondary mt-1">
              {START_YEAR}–{CURRENT_YEAR} · 20 regions · select a region to explore
            </p>
          </div>
          {isOwner && (
            <button onClick={onShowAddYear} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-accent-burgundy hover:bg-accent-burgundy/80 text-white text-sm font-medium transition-colors flex-shrink-0">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Year
            </button>
          )}
        </div>

        {/* Score legend */}
        <div className="flex items-center gap-1.5 mb-8 flex-wrap">
          <span className="text-xs text-text-secondary mr-1">Score:</span>
          {[
            { label: '95+',   bg: '#5C1825', fg: '#FFCFA8' },
            { label: '90–94', bg: '#C49255', fg: '#FFE0CC' },
            { label: '85–89', bg: '#7A3B1E', fg: '#FFD9B0' },
            { label: '80–84', bg: '#7D5A1A', fg: '#FFEBB0' },
            { label: '75–79', bg: '#7A6820', fg: '#FFF5D0' },
            { label: '70–74', bg: '#62583A', fg: '#F5EDD0' },
            { label: '65–69', bg: '#5C5040', fg: '#E8DDD0' },
            { label: '<65',   bg: '#4E4858', fg: '#DDD8E8' },
            { label: 'ND',    bg: '#1B202B', fg: '#9C9483' },
          ].map(({ label, bg, fg }) => (
            <span key={label} style={{ backgroundColor: bg, color: fg }} className="text-[11px] px-2 py-0.5 rounded-sm font-mono">
              {label}
            </span>
          ))}
        </div>

        {/* Region cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {regions.map((r) => (
            <RegionCard
              key={r.id}
              region={r}
              stats={regionStats.get(r.id)!}
              bottleCount={bottleCounts.get(r.id) ?? 0}
              onClick={() => onSelectRegion(r.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function VintageChartsPage() {
  const isOwner = useIsOwner();
  const isMobile = useIsMobile();
  const { data: allCharts, isLoading, isError } = useAllVintageCharts();
  const enqueueMutation = useEnqueueVintageChartYear();
  const { counts: bottleCounts } = useBottleCountByVintageChartRegion();
  const { data: customRegions = [] } = useCustomWineRegions();

  // Merge built-in regions with any owner-defined custom regions
  const allRegions = useMemo(
    () => [...REGIONS, ...customRegions.filter((cr) => !REGIONS.find((r) => r.id === cr.id))],
    [customRegions]
  );

  // Drill-down level (region grid -> region detail -> narrative) lives in the URL,
  // not useState, per STYLE_GUIDE.md ("state that affects URL -> searchParams").
  // Each transition pushes a history entry, so the browser/OS back button steps
  // back one level at a time instead of skipping straight out of the page.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeRegionId = searchParams.get('region');
  const selectedYear   = searchParams.get('year');
  const [showAddYear,  setShowAddYear]  = useState(false);

  const chartMap = useMemo(() => {
    const m = new Map<string, VintageChart>();
    for (const c of allCharts ?? []) m.set(`${c.region}_${c.year}`, c);
    return m;
  }, [allCharts]);

  const existingKeys  = useMemo(() => new Set(chartMap.keys()), [chartMap]);
  const activeRegion  = useMemo(() => allRegions.find((r) => r.id === activeRegionId) ?? null, [activeRegionId, allRegions]);
  const selected = useMemo(() => {
    if (!activeRegionId || !selectedYear) return null;
    return chartMap.get(`${activeRegionId}_${selectedYear}`) ?? null;
  }, [activeRegionId, selectedYear, chartMap]);

  const handleSelectRegion = useCallback((regionId: string) => {
    setSearchParams({ region: regionId });
  }, [setSearchParams]);

  const handleBack = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  const handleSelectEntry = useCallback((entry: VintageChart | null) => {
    if (!activeRegionId) return;
    setSearchParams(entry ? { region: activeRegionId, year: String(entry.year) } : { region: activeRegionId });
  }, [activeRegionId, setSearchParams]);

  const handleAddYear = useCallback((region: string, year: number) => {
    enqueueMutation.mutate({ region, year }, { onSuccess: () => setShowAddYear(false) });
  }, [enqueueMutation]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#1C1814' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent-burgundy border-t-transparent" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#1C1814' }}>
        <p className="text-sm text-red-400">Failed to load vintage charts.</p>
      </div>
    );
  }

  const isEmpty = (allCharts?.length ?? 0) === 0;

  if (isEmpty) {
    return (
      <div className="min-h-screen bg-surface-1 flex flex-col items-center justify-center gap-4 text-center px-8">
        <div className="text-4xl">📊</div>
        <h3 className="text-lg font-semibold text-text-primary">No vintage charts yet</h3>
        <p className="text-sm text-text-secondary max-w-sm">
          Run the seed batch to generate ~990 records for 20 regions ({START_YEAR}–{CURRENT_YEAR}).
        </p>
        <code className="text-xs bg-surface-2 px-3 py-2 rounded border border-border-medium text-text-secondary">
          npm run seed:vintage-charts
        </code>
      </div>
    );
  }

  // Desktop keeps a viewport-locked height so the table column and the
  // resizable narrative side-panel can each scroll independently. On mobile
  // there's no side panel to bound, and forcing 100dvh here (on top of the
  // TopBar's own height, inside AppShell's normal document flow) pushed the
  // page's true bottom below the fold, tucking the last rows under the fixed
  // BottomNav/AddBottleFAB. Mobile instead uses plain min-h-screen, same as
  // every other page in the app, and scrolls with the document.
  return (
    <div
      className="min-h-screen text-text-primary flex flex-col"
      style={isMobile ? { background: '#1C1814' } : { height: '100dvh', background: '#1C1814' }}
    >
      <div className={isMobile ? 'flex flex-col' : 'flex-1 overflow-hidden flex flex-col'}>
        {activeRegion ? (
          <RegionDetailView
            region={activeRegion}
            chartMap={chartMap}
            onBack={handleBack}
            onSelectEntry={handleSelectEntry}
            selectedEntry={selected}
            isOwner={isOwner}
            onAddYear={() => setShowAddYear(true)}
          />
        ) : (
          <RegionGrid
            chartMap={chartMap}
            regions={allRegions}
            onSelectRegion={handleSelectRegion}
            isOwner={isOwner}
            onShowAddYear={() => setShowAddYear(true)}
            bottleCounts={bottleCounts}
          />
        )}
      </div>

      {showAddYear && (
        <AddYearModal
          existingYears={existingKeys}
          onClose={() => setShowAddYear(false)}
          onAdd={handleAddYear}
          regions={allRegions}
        />
      )}
    </div>
  );
}
